import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DocListLegend } from "@/modules/core/components/DocListLegend";
import { POSTED_DOC_LIFECYCLE_LEGEND } from "@/modules/core/components/lifecycle-status";
import type { PostedDocLifecycleFilter } from "@/modules/core/list-cell-actions";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import {
  paramOr,
  readQ,
  setOrDelete,
  LIST_SEARCH_DEBOUNCE_MS,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DataTableChromeHost,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { SavedListViewsControl } from "@/modules/shell/SavedListViewsControl";
import { takeListReturn } from "@/modules/shell/list-return-state";
import { useListRowFlash } from "@/modules/shell/use-list-row-flash";
import { listReturns, type ReturnListSort } from "@/shared/api/returns";
import { listRecentPosTickets, voidPosTicket } from "@/shared/api/pos";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { returnListColumns } from "./returns-columns";

const PAGE_SIZE = 50;
const LIFECYCLES = ["all", "draft", "posted", "void"] as const;
const SORTABLE: ReturnListSort[] = [
  "number",
  "grossTotal",
  "lifecycle",
  "sourceType",
];

function sortingToApi(sorting: SortingState): {
  sort: ReturnListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as ReturnListSort)) {
    return {
      sort: first.id as ReturnListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "number", sortDir: "asc" };
}

export function ReturnsPage() {
  const { companySlug } = useParams();
  const confirmUi = useConfirm();
  const queryClient = useQueryClient();
  const { searchParams, patchParams, replaceAllParams } = useListUrlParams();
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const lifecycleFilter = paramOr(
    searchParams,
    "lifecycle",
    LIFECYCLES,
    "all",
  ) as PostedDocLifecycleFilter;
  const setLifecycleFilter = (next: PostedDocLifecycleFilter) =>
    patchParams((p) => setOrDelete(p, "lifecycle", next));
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);
  const serverLifecycle =
    lifecycleFilter === "all" ? undefined : lifecycleFilter;
  const listPath = companySlug
    ? `/c/${companySlug}/returns`
    : "/returns";
  const listReturnHandled = useRef(false);
  const { flashId, flashRow } = useListRowFlash();

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const returnsQuery = useInfiniteQuery({
    queryKey: queryKeys.returns.list(companySlug ?? "", {
      q: debouncedQ,
      lifecycle: serverLifecycle ?? "all",
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listReturns(companySlug!, {
        q: debouncedQ || undefined,
        lifecycle: serverLifecycle,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });

  const ticketsQuery = useQuery({
    queryKey: [...queryKeys.returns.page(companySlug ?? ""), "tickets"] as const,
    enabled: Boolean(companySlug),
    queryFn: () => listRecentPosTickets(companySlug!),
  });

  const errorMsg = queryErrorMessage(
    returnsQuery.error ?? ticketsQuery.error,
    returnsQuery.isError || ticketsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  async function reload() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.returns.all(companySlug),
    });
  }

  const columns = useMemo(
    () =>
      companySlug
        ? returnListColumns(companySlug, { listPath })
        : [],
    [companySlug, listPath],
  );

  const rows = returnsQuery.data?.pages.flatMap((p) => p.returns) ?? [];
  const visible = rows;
  const total = returnsQuery.data?.pages[0]?.total;
  const tickets = ticketsQuery.data?.tickets ?? [];
  const loading = returnsQuery.isPending;
  const filtersActive =
    Boolean(query.trim()) || lifecycleFilter !== "all";
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: returnsQuery.isFetching,
  });
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  useEffect(() => {
    if (!companySlug || loading || listReturnHandled.current) return;
    listReturnHandled.current = true;
    const id = takeListReturn(listPath);
    if (id) flashRow(id);
  }, [companySlug, loading, listPath, flashRow]);

  if (!companySlug) return null;

  return (
      <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <PageHeader
        title="Returns"
        description="Customer brought goods back — not the same as void"
        className="items-center"
        actions={
          <>
            <DataTableChromeHost />
            <Button asChild>
              <Link
                to={`/c/${companySlug}/returns/new`}
                className="no-underline"
              >
                New return
              </Link>
            </Button>
          </>
        }
      />
      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}
      <AdminFilters>
        <input
          className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
          placeholder="Search return number…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className={adminFilterControlClass}
          value={lifecycleFilter}
          onChange={(e) =>
            setLifecycleFilter(e.target.value as PostedDocLifecycleFilter)
          }
        >
          <option value="all">All lifecycle</option>
          <option value="draft">Draft</option>
          <option value="posted">Posted</option>
          <option value="void">Void</option>
        </select>
        <SavedListViewsControl
          listId="returns"
          companySlug={companySlug}
          currentQs={searchParams.toString()}
          onApplyQs={(qs) => {
            replaceAllParams(qs);
            const q = readQ(new URLSearchParams(qs));
            setQuery(q);
            setDebouncedQ(q);
          }}
        />
        <ActiveFilterChips
          chips={
            lifecycleFilter !== "all"
              ? [
                  {
                    key: "lifecycle",
                    label:
                      lifecycleFilter[0]!.toUpperCase() +
                      lifecycleFilter.slice(1),
                    onClear: () => setLifecycleFilter("all"),
                  },
                ]
              : []
          }
        />
      </AdminFilters>
      <ListSearchStatus online searching={searchingOnline} />
      {visible.length > 0 && total != null ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Showing {visible.length}
          {total > rows.length ? ` · loaded ${rows.length} of ${total}` : ""}
        </p>
      ) : null}
      {loading && (
        <Alert variant="muted" className="mt-8">
          Loading returns…
        </Alert>
      )}

      {!loading && visible.length === 0 && !displayError && (
        <div className="mt-8">
          <EmptyState
            title={filtersActive ? "No returns match" : "No returns yet"}
            description={
              filtersActive
                ? "Try clearing search or lifecycle filter."
                : "Use this when a customer brings goods back — not for cashier mistakes."
            }
            action={
              filtersActive ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => filterReset.onClearAll()}
                >
                  Clear filters
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link
                    to={`/c/${companySlug}/returns/new`}
                    className="no-underline"
                  >
                    New return
                  </Link>
                </Button>
              )
            }
          />
        </div>
      )}

      {visible.length > 0 ? (
        <>
          <DocListLegend kinds={POSTED_DOC_LIFECYCLE_LEGEND} />
          <DataTable
            listId="returns"
            companySlug={companySlug}
            columns={columns}
            data={visible}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
            highlightedRowId={flashId}
            defaultVisibility={{ settleMode: false, restock: false }}
            onRefresh={() => void returnsQuery.refetch()}
          />
          {returnsQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={returnsQuery.isFetchingNextPage}
                pending={returnsQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void returnsQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {!loading && (
        <section className="mt-10 border-t border-[var(--color-border)] pt-6">
          <h2 className="m-0 text-sm font-semibold text-[var(--color-muted)]">
            Void a POS ticket
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Mistake or cancel on the register — not a customer return.
          </p>
          {tickets.length === 0 ? (
            <Alert variant="muted" className="mt-4 text-xs">
              No posted tickets to void. Ring a sale on POS first.
            </Alert>
          ) : (
            <AdminList>
              {tickets.slice(0, 8).map((t) => (
                <AdminListRow key={t.id}>
                  <Link
                    to={`/c/${companySlug}/pos/tickets/${t.id}`}
                    className="text-sm no-underline"
                  >
                    {t.number}{" "}
                    <span className="font-mono text-xs text-[var(--color-muted)]">
                      {t.grossTotal}
                    </span>
                  </Link>
                  <div className="flex shrink-0 gap-2">
                    <Button type="button" variant="ghost" size="sm" asChild>
                      <Link
                        to={`/c/${companySlug}/pos/tickets/${t.id}`}
                        className="no-underline"
                      >
                        Open
                      </Link>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busyId === t.id}
                      onClick={() => {
                        if (!companySlug) return;
                        void (async () => {
                          const ok = await confirmUi({
                            title: `Void ${t.number}?`,
                            description:
                              "Stock will restock. Voiding is permanent — you cannot undo this ticket.",
                            confirmLabel: "Void",
                            destructive: true,
                          });
                          if (!ok) return;
                          setBusyId(t.id);
                          setError(null);
                          try {
                            await voidPosTicket(companySlug, t.id, {
                              reason: "cashier void",
                              idempotencyKey: `void-${t.id}`,
                            });
                            reload();
                          } catch (err: unknown) {
                            setError(
                              err instanceof Error
                                ? err.message
                                : "Void failed",
                            );
                          } finally {
                            setBusyId(null);
                          }
                        })();
                      }}
                    >
                      Void
                    </Button>
                  </div>
                </AdminListRow>
              ))}
            </AdminList>
          )}
        </section>
      )}
      <p className="mt-8 text-sm">
        <Link to={`/c/${companySlug}`} className="no-underline">
          ← Company home
        </Link>
      </p>
      </DataTableChrome>
    </PageFrame>
  );
}
